/**
 * One glyph per KIND of work the guide does (round 3, owner: "can we have
 * unique icons for each different category of actions?").
 *
 * The activity chips used to carry an identical dot, so a run of five of
 * them read as one undifferentiated wait. A reader who glances at the
 * transcript should be able to tell "it went and read code" from "it went
 * and looked at the live infrastructure" without reading a word.
 *
 * Eight categories cover the twelve-tool registry (api.md 14) plus the
 * retired canvas tools that persisted display_json still replays. Anything
 * unknown falls to a neutral ring rather than borrowing a meaning it does
 * not have.
 *
 * The glyphs are inline SVG in the existing idiom: 14px, `currentColor`,
 * stroke 1.7, no fill, so they inherit the chip's colour in both themes.
 */

export type ToolCategory =
  | 'map'
  | 'code'
  | 'definitions'
  | 'path'
  | 'history'
  | 'live'
  | 'schedule'
  | 'diagram'
  | 'other';

/** Tool id to category. Retired tool ids keep working (legacy replay). */
const TOOL_CATEGORIES: Record<string, ToolCategory> = {
  search_map: 'map',
  get_node: 'map',
  get_flow: 'path',
  trace_route: 'path',
  read_file: 'code',
  get_symbol: 'definitions',
  get_schema: 'definitions',
  search_history: 'history',
  get_changes: 'history',
  get_live_infra: 'live',
  simulate_schedule: 'schedule',
  show_diagram: 'diagram',
  // Retired (pre-redesign display_json replays through here):
  canvas_focus: 'map',
  canvas_highlight: 'map',
  canvas_clear: 'map',
  canvas_step: 'path',
  propose_knowledge_update: 'other',
};

/** The category a tool id belongs to; unknown ids read as `other`. */
export function toolCategory(tool: string): ToolCategory {
  return TOOL_CATEGORIES[tool] ?? 'other';
}

const PATHS: Record<ToolCategory, React.ReactNode> = {
  // a folded map
  map: (
    <>
      <path d="M9.5 4.5 3.5 6.5v13l6-2 5 2 6-2v-13l-6 2z" />
      <path d="M9.5 4.5v13M14.5 6.5v13" />
    </>
  ),
  // code brackets
  code: (
    <>
      <path d="M8.5 7 4 12l4.5 5" />
      <path d="M15.5 7 20 12l-4.5 5" />
    </>
  ),
  // curly braces: a definition
  definitions: (
    <>
      <path d="M9.5 4.5h-1a2 2 0 0 0-2 2V9a3 3 0 0 1-2 3 3 3 0 0 1 2 3v2.5a2 2 0 0 0 2 2h1" />
      <path d="M14.5 4.5h1a2 2 0 0 1 2 2V9a3 3 0 0 0 2 3 3 3 0 0 0-2 3v2.5a2 2 0 0 1-2 2h-1" />
    </>
  ),
  // two stops and the route between them
  path: (
    <>
      <circle cx="6" cy="6" r="2.2" />
      <circle cx="18" cy="18" r="2.2" />
      <path d="M6 8.2v5.3a4 4 0 0 0 4 4h5.8" />
    </>
  ),
  // a clock wound backwards
  history: (
    <>
      <path d="M3.6 12a8.4 8.4 0 1 0 2.6-6.1" />
      <path d="M3.5 4.5v4h4" />
      <path d="M12 8.2V12l2.6 1.5" />
    </>
  ),
  // a heartbeat: something running right now
  live: <path d="M3 12h3.5L9 6.5l3.4 11L15 12h6" />,
  // a calendar
  schedule: (
    <>
      <rect x="3.5" y="5.5" width="17" height="14" rx="2" />
      <path d="M8 3.5v4M16 3.5v4M3.5 10.5h17" />
    </>
  ),
  // two boxes and the link between them
  diagram: (
    <>
      <rect x="3.5" y="4.5" width="7" height="5" rx="1.5" />
      <rect x="13.5" y="14.5" width="7" height="5" rx="1.5" />
      <path d="M10.5 7h3A1.5 1.5 0 0 1 15 8.5V17" />
    </>
  ),
  other: <circle cx="12" cy="12" r="4.5" />,
};

/**
 * The category glyph for one tool. `running` pulses it (the chip's old
 * job): the work is still in flight, the summary has not landed yet.
 */
export function ToolGlyph({ tool, running = false }: { tool: string; running?: boolean }) {
  const category = toolCategory(tool);
  return (
    <svg
      data-testid="tool-glyph"
      data-tool-category={category}
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${running ? 'lm-pulse' : ''}`}
      aria-hidden
    >
      {PATHS[category]}
    </svg>
  );
}
